/**
 * <cart-drawer> - primaer kurv-UX, foerste rigtige forbruger af
 * @theme/section-render. Progressive enhancement: uden JS navigerer header-
 * linket til /cart (cart.json fuld side). Med JS opgraderes det til en drawer.
 *
 * Penge/locale: totaler og linjepriser kommer ALTID server-renderet retur via
 * Section Rendering (sections-parameteren) - intet formateres client-side.
 * Re-render bytter kun [data-cart-content], saa skallen (dialog, focus-trap,
 * aria-live) overlever. <dialog>.showModal() giver focus-trap, Escape og
 * inert baggrund nativt; vi laegger focus-retur + aria oven paa.
 *
 * To kontroller taler til serveren herfra, og begge gaar gennem den samme
 * serialiserede koe (#mutate):
 *   [data-cart-quantity] - den delte <quantity-input>; dens boblende 'change'
 *                          baerer den absolutte vaerdi (skrevet eller steppet)
 *   [data-cart-remove]   - fjern linje (change, quantity 0)
 *   [data-cart-bundle-remove] - fjern alle linjer i et bundle (update, 0)
 */
import { cartRequest, swapInner, SectionRenderError } from '@theme/section-render';

const SECTION = '[id^="shopify-section-"]';
// Isoleret taeller-sektion (statisk filnavn-id). Re-renderes alene, saa kurv-
// mutationer ikke koster en hel header-render (THEME-SPEC 3). Se sections/cart-icon.liquid.
const CART_ICON_SECTION = 'cart-icon';

// Bundle-formularen (blocks/bundle-form) sender items[n][id|quantity] og
// items[n][properties][navn]. Samles til /cart/add.js' items-array, saa alle
// dele lander i ét kald.
function itemsFromForm(formData) {
  const items = [];
  for (const [name, value] of formData) {
    const match = /^items\[(\d+)\]\[(id|quantity|properties)\](?:\[([^\]]+)\])?$/.exec(name);
    if (!match) continue;
    const item = items[match[1]] || (items[match[1]] = { properties: {} });
    if (match[2] === 'properties') {
      if (match[3]) item.properties[match[3]] = value;
    } else {
      item[match[2]] = Number(value);
    }
  }
  return items.filter((item) => item && item.id);
}

function sectionIdOf(el) {
  const section = el && el.closest(SECTION);
  return section ? section.id.replace('shopify-section-', '') : null;
}

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-cart-dialog]');
    this.content = this.querySelector('[data-cart-content]');
    this.titleNode = this.querySelector('[data-cart-title]');
    this.live = this.querySelector('[data-cart-live]');
    this.errorBox = this.querySelector('[data-cart-error]');
    this.opener = null;

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('close', () => {
      this.#setExpanded(false);
      if (this.opener && document.contains(this.opener)) this.opener.focus();
    });
    this.addEventListener('click', this.#onClick);
    this.content.addEventListener('change', this.#onChange);

    CartDrawer.instance = this;
    if (!CartDrawer.documentBound) {
      document.addEventListener('click', CartDrawer.onTriggerClick);
      document.addEventListener('submit', CartDrawer.onProductSubmit);
      CartDrawer.documentBound = true;
    }
  }

  open(opener) {
    if (opener) this.opener = opener;
    if (!this.dialog.open) this.dialog.showModal();
    this.#setExpanded(true);
    this.#clearError();
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  #onClick = (event) => {
    if (event.target.closest('[data-cart-close]')) {
      this.close();
      return;
    }

    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      // CUSTOM: r02-b1 - adresser linjen via den stabile lineRef, ikke den roterende key.
      this.#mutate('change', {
        lineRef: remove.dataset.lineRef,
        quantity: 0,
        title: remove.dataset.lineTitle,
      });
      return;
    }

    const bundleRemove = event.target.closest('[data-cart-bundle-remove]');
    if (bundleRemove) {
      event.preventDefault();
      this.#mutate('update', {
        bundleId: bundleRemove.dataset.bundleId,
        title: bundleRemove.dataset.bundleTitle,
      });
    }
  };

  #onChange = (event) => {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input) return;
    /* === CUSTOM: r02-c10 === */
    // Tomt/ugyldigt felt er IKKE en fjern-intention: gendan server-vaerdien (input
    // renderes friskt pr. swap, saa defaultValue = aktuel qty) og lad vaere. En
    // eksplicit 0 fjerner stadig (og [data-cart-remove] findes altid).
    const parsed = parseInt(input.value, 10);
    if (input.value.trim() === '' || Number.isNaN(parsed) || parsed < 0) {
      input.value = input.defaultValue;
      return;
    }
    /* === END CUSTOM: r02-c10 === */
    // CUSTOM: r02-b1 - adresser linjen via den stabile lineRef, ikke den roterende key.
    this.#mutate('change', {
      lineRef: input.dataset.lineRef,
      quantity: parsed,
      title: input.dataset.lineTitle,
    });
  };

  /* === CUSTOM: r02-b1 === */
  // Serialiser kurv-mutationer: hver venter paa den forrige, saa svar lander i
  // raekkefoelge og den endelige render afspejler den endelige kurv (retter B1's
  // last-response-wins). En afvist mutation braekker ikke koeen for den naeste.
  //
  // NB: denne butik gen-hasher ALLE line-keys ved enhver kurv-skrivning, saa en key
  // fanget ved enqueue er foraeldet naar en tidligere koe-mutation er faerdig. Derfor
  // baerer 'change'-mutationer en STABIL lineRef (variant+selling_plan), og #run
  // slaar den aktuelle key op fra den friske DOM lige foer kaldet.
  #chain = Promise.resolve();

  #mutate(endpoint, body, opener) {
    const run = () => this.#run(endpoint, body, opener);
    this.#chain = this.#chain.then(run, run);
    return this.#chain;
  }
  /* === END CUSTOM: r02-b1 === */

  async #run(endpoint, body, opener) {
    // Double-submit-vagten ligger i kalderen (onProductSubmit), ikke her.
    const cartId = sectionIdOf(this);
    const sections = [cartId, CART_ICON_SECTION].filter(Boolean);
    /* === CUSTOM: r02-b1 === */
    // Genskab payload: for en 'change' via lineRef slaas den AKTUELLE key op fra den
    // friske DOM (keys roterer ved hver skrivning). Findes linjen ikke laengere (fjernet
    // af en tidligere koe-mutation), droppes kaldet - ingen 400 paa en doed key.
    let payload = body;
    let changed = null;
    if (endpoint === 'change' && body && body.lineRef != null) {
      const key = this.#currentKey(body.lineRef);
      if (!key) return null;
      payload = { id: key, quantity: body.quantity };
      changed = { lineRef: body.lineRef, title: body.title || '' };
    }
    // Bundle: keys roterer ogsaa her, saa gruppens AKTUELLE keys slaas op fra
    // den friske DOM lige foer kaldet. Alle saettes til 0 i ét update-kald.
    let removedBundle = null;
    if (endpoint === 'update' && body && body.bundleId != null) {
      const updates = {};
      this.content
        .querySelectorAll(`[data-bundle-line="${CSS.escape(body.bundleId)}"][data-bundle-key]`)
        .forEach((line) => {
          updates[line.dataset.bundleKey] = 0;
        });
      if (Object.keys(updates).length === 0) return null;
      payload = { updates };
      removedBundle = body.title || '';
    }
    /* === END CUSTOM: r02-b1 === */
    // Hvilken kontrol havde fokus? swapInner river den gamle node ud, saa vi
    // skal genskabe fokus inde i dialogen bagefter (focus-management, 6.2). Vi
    // matcher paa lineRef (stabil), ikke key (roterer ved hver skrivning).
    const focusSelector = CartDrawer.focusSelectorOf(document.activeElement);
    this.setAttribute('aria-busy', 'true');
    // Busy-state paa selve den udloesende knap (spinner via .btn[aria-busy],
    // base.css) - drawerens egen aria-busy ses ikke foer den er aaben.
    const busyButton = opener && opener.matches && opener.matches('button, [type="submit"]') ? opener : null;
    if (busyButton) busyButton.setAttribute('aria-busy', 'true');
    try {
      const data = await cartRequest(endpoint, payload, { sections });
      // Sektioner kan komme tilbage som null med HTTP 200 - haandter det.
      if (cartId && data.sections && data.sections[cartId]) {
        // Accept-fluebenet er klient-tilstand i den markup swapInner erstatter;
        // uden at baere det over fjerner en antalsaendring det i stilhed. Laeses
        // FOERST NU, ikke foer kaldet: et flueben sat med mellemrum mens
        // requesten koerte, ville ellers blive overskrevet med den gamle vaerdi.
        const terms = this.content.querySelector('[data-cart-terms]');
        const termsAccepted = Boolean(terms && terms.checked);
        swapInner(this.content, data.sections[cartId], '[data-cart-content]');
        const freshTerms = this.content.querySelector('[data-cart-terms]');
        if (freshTerms) freshTerms.checked = termsAccepted;
      } else {
        throw new SectionRenderError('cart-drawer renderede tomt');
      }
      this.#syncTitle();
      if (data.sections && data.sections[CART_ICON_SECTION]) {
        this.#swapBubble(data.sections[CART_ICON_SECTION]);
      }
      if (endpoint === 'add') {
        // Et add fra et andet modal (quick-view) skal efterlade kurven alene.
        // 'cart:added' bobler fra submit-knappen; modalet lukker sig og kan
        // give sin EGEN opener retur i detail - submit-knappen forsvinder med
        // modalets indhold, og en fokus-retur til den ville falde til <body>.
        const handoff = new CustomEvent('cart:added', { bubbles: true, detail: { opener } });
        if (opener && typeof opener.dispatchEvent === 'function') opener.dispatchEvent(handoff);
        // Overtag KUN en opener der stadig sidder i dokumentet. Et add fra et
        // upsalgskort inde i [data-cart-content] efterlader BEGGE kandidater
        // detached: swapInner har allerede revet upsalgsknappen ud, og quick-view
        // rydder sit eget indhold naar det lukker. Uden vagten ville this.opener
        // blive sat til en doed node, og drawerens fokus-retur ved luk (dialog
        // 'close') ville tavst no-op'e resten af sessionen.
        const handedOff = handoff.detail.opener;
        this.open(handedOff && document.contains(handedOff) ? handedOff : undefined);
        this.#announceAdded(data, opener);
      } else if (changed) {
        this.#announceChange(changed);
      } else if (removedBundle != null && this.dataset.textRemoved) {
        this.#announce(this.dataset.textRemoved.replace('%TITLE%', removedBundle) + this.#totalPhrase());
      }
      this.#restoreFocus(focusSelector);
      this.#clearError();
      return data;
    } catch (error) {
      // Aabn drawer ved fejlet add, saa den synlige fejl faktisk ses.
      if (endpoint === 'add') this.open(opener);
      this.#showError(error);
      return null;
    } finally {
      this.removeAttribute('aria-busy');
      if (busyButton) busyButton.removeAttribute('aria-busy');
    }
  }

  /* === CUSTOM: r02-b1 === */
  // Slaa den aktuelle line-key op fra den friske DOM ud fra en stabil lineRef.
  // Bruges lige foer et 'change'-kald, saa vi aldrig sender en roteret/doed key.
  #currentKey(lineRef) {
    if (lineRef == null) return null;
    const el = this.content.querySelector(`[data-line-ref="${CSS.escape(lineRef)}"][data-cart-key]`);
    return el ? el.dataset.cartKey : null;
  }
  /* === END CUSTOM: r02-b1 === */

  // Headeren ligger i skallen og re-renderes ikke, men titlen baerer varetallet.
  // [data-cart-title-source] er serverens friske, fuldt oversatte streng inde i
  // det swappede indhold - vi flytter teksten, vi samler den ikke.
  #syncTitle() {
    const source = this.content.querySelector('[data-cart-title-source]');
    if (source && this.titleNode) this.titleNode.textContent = source.textContent.trim();
  }

  // Fokus efter en re-render: samme kontrol som foer, hvis den stadig findes,
  // ellers luk-knappen (altid i skallen). Kun hvis fokus rent faktisk gik tabt
  // (faldt til <body>) - swapInner river den gamle node ud under foedderne.
  static focusSelectorOf(el) {
    if (!el || !el.dataset) return null;
    if (el.dataset.cartTerms != null) return '[data-cart-terms]';
    const ref = el.dataset.lineRef;
    if (ref == null) return null;
    if (el.dataset.cartQuantity != null) {
      return `[data-cart-quantity][data-line-ref="${CSS.escape(ref)}"]`;
    }
    if (el.dataset.cartRemove != null) {
      return `[data-cart-remove][data-line-ref="${CSS.escape(ref)}"]`;
    }
    return null;
  }

  #restoreFocus(selector) {
    if (!this.dialog.open) return;
    if (document.activeElement && document.activeElement !== document.body) return;
    const target = (selector && this.content.querySelector(selector)) || this.querySelector('[data-cart-close]');
    if (target) target.focus();
  }

  #swapBubble(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cart-bubble]');
    const current = document.querySelector('[data-cart-bubble]');
    if (fresh && current) {
      current.replaceWith(fresh);
      // pop-mikroanimation KUN ved mutation (klassen findes ikke i server-
      // markup'en, saa initial page load popper aldrig). CSS: header.liquid.
      fresh.classList.add('cart-bubble--pop');
    }
  }

  // Ordretotalen laeses fra den FRISKE server-renderede node - aldrig
  // genberegnet. Det er samme beloeb som checkout-knappen viser, og i
  // modsaetning til Subtotal-raekken er det entydigt naar der er rabat paa
  // kurven (se kommentaren ved [data-cart-total] i sections/cart-drawer.liquid).
  // Tom naar footeren ikke findes - fx da den sidste vare blev fjernet.
  #totalPhrase() {
    const node = this.content.querySelector('[data-cart-total]');
    const total = node ? node.textContent.trim() : '';
    if (!total) return '';
    return `. ${this.dataset.textTotal || ''} ${total}`;
  }

  #announceAdded(data, opener) {
    const added = this.dataset.textAdded || '';
    const first = data.items && data.items[0];
    // Et bundle (flere varer i ét kald) annonceres med bundlets navn, ikke
    // den foerste dels - formen baerer det server-renderet i data-cart-title.
    const named = opener && opener.closest ? opener.closest('[data-cart-title]') : null;
    const title =
      (named && named.dataset.cartTitle) ||
      (first && (first.product_title || first.title)) ||
      data.product_title ||
      data.title ||
      '';
    const message = title ? `${title}: ${added}` : added;
    this.#announce(message + this.#totalPhrase());
  }

  // Stepperen aendrer ingen synlig tekst for en skaermlaeser - uden det her
  // ville et klik paa + vaere tavst.
  //
  // Antallet laeses ud af den FRISKE, server-renderede node og ikke af det vi
  // bad om: /cart/change.js svarer 200 og KLIPPER til lagerbeholdning eller en
  // quantity rule, saa "sæt 4" kan meget vel ende som 3. Annoncerede vi vores
  // eget oenske, ville oplaesningen modsige det synlige felt. Findes linjen
  // ikke laengere, blev den fjernet.
  #announceChange({ lineRef, title }) {
    const node = this.content.querySelector(`[data-cart-quantity][data-line-ref="${CSS.escape(lineRef)}"]`);
    const label = node ? this.dataset.textQuantity : this.dataset.textRemoved;
    if (!label) return;
    const message = label.replace('%TITLE%', title).replace('%QTY%', node ? node.value : '');
    this.#announce(message + this.#totalPhrase());
  }

  #announce(message) {
    if (!this.live) return;
    this.live.textContent = '';
    requestAnimationFrame(() => {
      this.live.textContent = message;
    });
  }

  #showError(error) {
    // Kun Shopifys egne, kundeskrevne beskeder gaar i fejlboksen. Alt andet er
    // diagnostik og ville ellers lande foran kunden som "Uventet ikke-JSON svar
    // fra cart/add.js (401)" - hvad der i praksis sker naar butikkens
    // bot-beskyttelse svarer med en HTML-challenge i stedet for JSON (typisk
    // paa en lokal `shopify theme dev`, hvor browserens clearance-cookie ikke
    // gaelder for 127.0.0.1). Den tekniske aarsag beholdes i konsollen.
    const fallback = this.dataset.textError || '';
    const message = error && error.customerFacing && error.message ? error.message : fallback;
    if (error && !error.customerFacing) console.warn('[cart-drawer]', error);
    if (this.errorBox) {
      this.errorBox.textContent = message;
      this.errorBox.hidden = false;
    }
    // errorBox er role=alert: den annoncerer selv naar drawer er aaben. Brug kun
    // den polite live-region naar drawer er lukket, saa det ikke laeses dobbelt.
    if (!this.dialog.open) this.#announce(message);
  }

  #clearError() {
    if (this.errorBox) {
      this.errorBox.hidden = true;
      this.errorBox.textContent = '';
    }
  }

  #setExpanded(expanded) {
    document.querySelectorAll('[data-cart-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(expanded));
    });
  }

  static onTriggerClick(event) {
    const trigger = event.target.closest('[data-cart-trigger]');
    if (!trigger) return;
    const drawer = CartDrawer.instance;
    if (!drawer) return; // ingen enhancement-target -> linket navigerer til /cart
    event.preventDefault();
    drawer.open(trigger);
  }

  static onProductSubmit(event) {
    // En komponent paa formen (fx bundle-form.js' validering) har stoppet den.
    if (event.defaultPrevented) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!(form.getAttribute('action') || '').includes('/cart/add')) return;
    // /cart-sidens gavevaelger (snippets/cart-gift) skal POSTe normalt, saa
    // siden genindlaeses med gaven i stedet for at kun draweren opdateres.
    if (form.hasAttribute('data-cart-native')) return;
    const drawer = CartDrawer.instance;
    if (!drawer) return; // no-JS / ingen drawer -> normal POST til /cart/add
    // Flere varer i ét kald (blocks/bundle-form): alle eller ingen.
    if (form.hasAttribute('data-cart-items')) {
      const items = itemsFromForm(new FormData(form));
      if (items.length === 0) return;
      event.preventDefault();
      const submitter = event.submitter || form.querySelector('[type="submit"]') || form;
      if (submitter.getAttribute && submitter.getAttribute('aria-busy') === 'true') return;
      drawer.#mutate('add', { items }, submitter);
      return;
    }
    // submitter-parameteren medtager den klikkede knaps name/value - baerende
    // for quick-shoppens size-knapper og for kurvens EGET upsalgs-
    // stoerrelsesgitter (name='id' value=variant.id, KCA 4.6).
    const formData = new FormData(form, event.submitter || undefined);
    const id = formData.get('id');
    if (!id) return; // lad serveren haandtere en ufuldstaendig form
    event.preventDefault();
    const opener = event.submitter || form.querySelector('[type="submit"]') || form;
    // Genindsendelse fra en allerede-busy knap (Enter paa fokuseret knap -
    // pointer-events daekker kun mus) -> drop; ellers dobbelt-add.
    if (opener.getAttribute && opener.getAttribute('aria-busy') === 'true') return;
    const item = { id, quantity: Number(formData.get('quantity') || 1) };
    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) item.selling_plan = sellingPlan;
    // Gave ved koeb (blocks/gift-offer + gift-picker.js): gift_id hoerer til
    // formen via form=. Gaven laegges i kurven EFTER varen og kun hvis varen
    // kom i - et faelles kald afvises helt hvis gaven er udsolgt, og saa roeg
    // varen med. Fejler gaven, viser kurven fejlen, og kurvens egen
    // gavevaelger (snippets/cart-gift) tilbyder den igen.
    //
    // Serveren afgoer om der er plads til en gave: kurvens vaelger
    // ([data-cart-gift-pick]) renderes kun naar en udloeser ligger i kurven
    // UDEN en gave. Mangler den efter varens add, ligger der allerede en gave
    // (rabatten giver kun én gratis), og den valgte springes over. Imens
    // skjuler data-gift-pending kurvens vaelger, saa et klik dér ikke kan
    // laegge en gave nummer to i koeen.
    const gift = formData.get('gift_id');
    if (gift) drawer.setAttribute('data-gift-pending', '');
    const added = drawer.#mutate('add', { items: [item] }, opener);
    if (gift) {
      added
        .then((data) => {
          if (!data || !drawer.content.querySelector('[data-cart-gift-pick]')) return null;
          return drawer.#mutate('add', { items: [{ id: gift, quantity: 1 }] });
        })
        .finally(() => drawer.removeAttribute('data-gift-pending'));
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
